import { SoftphoneCallState } from "@/contexts/SoftphoneContext";
import {
  faGrip,
  faPause,
  faMicrophone,
  faMicrophoneSlash,
  faPhoneFlip,
  faPhoneSlash,
  faVolumeHigh,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, useState } from "react";
import { Button, Collapse } from "react-bootstrap";
import { KEYS } from "./WebphoneKeypad";

interface IProps {
  callState: SoftphoneCallState;
  muted: boolean;
  number?: string | null;
  busy?: boolean;
  onHold?: () => void;
  onHangup: () => void;
  onToggleMute: () => void;
  onDtmf: (digit: string) => void;
}

const WebphoneInCall: FC<IProps> = ({ callState, muted, number, busy = false, onHold, onHangup, onToggleMute, onDtmf }) => {
  const [showKeypad, setShowKeypad] = useState(false);
  const inCall = callState === "in-call";

  return (
    <div className="px-3 pb-4 text-center">
      <FontAwesomeIcon
        icon={callState === "calling" ? faPhoneFlip : faVolumeHigh}
        className="fs-1 text-body-secondary mb-1"
      />
      <p className="fw-semi-bold text-break mb-1">{number ?? "Numéro indisponible"}</p>
      <p className="fs--1 text-body-secondary mb-3">
        {callState === "calling" ? "Appel en cours…" : "En communication"}
      </p>

      {inCall && (
        <Collapse in={showKeypad}>
          <div className="webphone-keypad-grid mb-3">
            {KEYS.map(({ digit, letters }) => (
              <button
                key={digit}
                type="button"
                className="webphone-key"
                disabled={busy}
                onClick={() => onDtmf(digit)}
                aria-label={letters ? `${digit}, ${letters.split("").join(" ")}` : digit}
              >
                <span className="webphone-key-digit">{digit}</span>
                {letters && <span className="webphone-key-letters">{letters}</span>}
              </button>
            ))}
          </div>
        </Collapse>
      )}

      <div className="webphone-active-actions d-flex justify-content-center gap-2">
        <div className="text-center">
          <Button
            variant={muted ? "warning" : "nolina-default"}
            className="webphone-action-btn"
            aria-label={muted ? "Réactiver le micro" : "Couper le micro"}
            aria-pressed={muted}
            disabled={busy || !inCall}
            onClick={onToggleMute}
          >
            <FontAwesomeIcon icon={muted ? faMicrophoneSlash : faMicrophone} />
          </Button>
          <p className="fs--1 text-body-secondary mt-2 mb-0">
            {muted ? "Micro coupé" : "Muet"}
          </p>
        </div>
        {inCall && onHold && (
          <div className="text-center">
            <Button variant="nolina-default" className="webphone-action-btn" aria-label="Mettre en attente" disabled={busy} onClick={onHold}>
              <FontAwesomeIcon icon={faPause} />
            </Button>
            <p className="fs--1 text-body-secondary mt-2 mb-0">Attente</p>
          </div>
        )}
        {inCall && (
          <div className="text-center">
            <Button
              variant={showKeypad ? "warning" : "nolina-default"}
              className="webphone-action-btn"
              aria-label="Afficher le clavier"
              aria-pressed={showKeypad}
              onClick={() => setShowKeypad((old) => !old)}
            >
              <FontAwesomeIcon icon={faGrip} />
            </Button>
            <p className="fs--1 text-body-secondary mt-2 mb-0">Clavier</p>
          </div>
        )}
        <div className="text-center">
          <Button
            variant="danger"
            className="webphone-action-btn"
            aria-label="Raccrocher"
            onClick={onHangup}
          >
            <FontAwesomeIcon icon={faPhoneSlash} />
          </Button>
          <p className="fs--1 text-body-secondary mt-2 mb-0">Raccrocher</p>
        </div>
      </div>
    </div>
  );
};

export default WebphoneInCall;
